import React, { useEffect, useRef, useState } from "react";

/** Nextjs */
import dynamic from "next/dynamic";

/** MUI */
import { Alert, Box, Grid, IconButton, Paper, TablePagination, alpha, useMediaQuery, Skeleton } from "@mui/material";
import { makeStyles } from "@mui/styles";
/** - icons */
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

/** Redux */
import { useDispatch, useSelector } from "react-redux";
import { getLoadingModules, setLoadingModules } from "@/src/_appStore/DB_Common";
import { getDatesSelected } from "@/src/_appStore/DB_Dates";
import {
    getCurrentSection,
    getDimensions,
    getInfoTypes,
    getMultiSections,
    getSections,
    getSelectedDimensions,
    getSelectedPositions,
    getFiltersChanged,
    setFiltersChanged,
    getLoadedLang,
} from "@/src/_appStore/DB_Filters_Table";
import { getSelectedIndicators, getCurrentIndicator } from "@/src/_appStore/DB_Indicators";
import { getSelectedTerritorialUnitsItems } from "@/src/_appStore/DB_JTs";
import { getSelectedJT, getSelectedSC, getSelectedWSK } from "@/src/_appStore/DB_Lists_Table";
import { getLayout, getOptions, getSorting, setSorting } from "@/src/_appStore/DB_Settings_Table";

/** Helpers */
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import useHelper from "@/src/_common/useHelper";

/** API */
import { getTableNewManyIndicatorsNewFromAPI } from "@/src/_APIs";

/** UI */
import "overlayscrollbars/overlayscrollbars.css";

const AppTooltip = dynamic(() => import("../../../../_common/AppTooltip"));
const Preloader = dynamic(() => import("../../../../_common/Preloader"));
const SplitView = dynamic(import("../SplitView/SplitView"));
const MuiTable = dynamic(() => import("./components/Table"));
const TableDialog = dynamic(() => import("../Dialog/TableDialog"));
// const ProgressAPI = dynamic(() => import("./components/ProgressAPI"));
const ProgressAPI = dynamic(() => import("@/src/_common/ProgressAPI"));
const Legend = dynamic(() => import("../Legend/Legend"));

const TablePreloader = () => {
    return (
        <Grid container spacing={5} sx={{ my: 5 }}>
            {Array(30)
                .fill()
                .map((i) => (
                    <Grid item xs={2} key={i} style={{ paddingTop: 2 }}>
                        <Skeleton
                            variant="rectangular"
                            height={4}
                            // animation={"wave"}
                            sx={{ backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2) }}
                        />
                    </Grid>
                ))}
        </Grid>
    );
};

const useStyles = makeStyles((theme) => ({
    osScrollbar: {
        // height: "calc(100vh - 360px)",
        // [theme.breakpoints.down("sm")]: {
        //     height: "calc(100vh - 336px)",
        // },
        "& > .os-scrollbar > .os-scrollbar-track > .os-scrollbar-handle, & > .os-scrollbar:hover > .os-scrollbar-track > .os-scrollbar-handle":
            {
                background: "#919090", // theme.palette.primary.main,
            },

        "& > .os-scrollbar.os-scrollbar-vertical": {
            padding: 1,
            width: 6,
            transition: "all 0.125s linear",
            background: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 16,
            "&:hover": {
                width: 16,
                padding: 2,
                cursor: "s-resize",
            },
        },
    },
    osScrollbarInDialog: {
        height: "calc(100vh - 360px)",
    },
    // tablePagination: {
    //     [theme.breakpoints.down("sm")]: {
    //         fontSize: "0.77777778rem",
    //     },
    // },
    // tablePaginationSelectLabel: {
    //     [theme.breakpoints.down("sm")]: {
    //         fontSize: "0.77777778rem",
    //     },
    // },

    tablePagination: {
        borderTopColor: "transparent",
        [theme.breakpoints.down("sm")]: {
            fontSize: "0.77777778rem",
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        },
    },
    tablePaginationToolbar: {
        [theme.breakpoints.down("sm")]: {
            flexWrap: "wrap",
            padding: theme.spacing(0, 0.5),
            justifyContent: "space-between",
            "& > div": {
                margin: 0,
            },
        },
    },
    tablePaginationSelect: {
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(0.5, 0, 0, 0),
        },
    },
    tablePaginationSelectLabel: {
        [theme.breakpoints.down("sm")]: {
            fontSize: "0.77777778rem",
            margin: 0,
            flexGrow: 1,
            textAlign: "right",
            minWidth: 170,
        },
    },
    tablePaginationDisplayedRows: {
        [theme.breakpoints.down("sm")]: {
            fontSize: "0.77777778rem",
            margin: 0,
            flexGrow: 1,
            minWidth: 170,
            minHeight: 40,
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
        },
    },
    tablePaginationActions: {
        [theme.breakpoints.down("sm")]: {
            flexGrow: 1,
            display: "flex",
            justifyContent: "space-between",
            marginLeft: "0!important",
            position: "absolute",
            bottom: 0,
            width: "98%",
            "& button": {
                // padding: 0,
            },
        },
    },
}));

const MuiTableIndex = (props) => {
    const {
        apiData = null,
        empty = null,
        isInDialog,
        loadingDefaults,
        onGetHeadData,
        onGetLegendData,
        onZoom,
        pageLength,
        query,
        zoom,
        onGetRequest,
        onApply,
        onAbortAPI,
    } = props;
    // process.env.NODE_ENV === "development" && console.log({ apiData });

    const classes = useStyles();
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { translationFromJson, getAllIDs } = useHelper();
    const paperRef = useRef();
    const abortControllerRef = useRef();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

    const defaultTableParams = {
        page: 0,
        offset: 0,
        rowsPerPage: 10,
        sort: {
            order: "asc",
            orderBy: null,
        },
    };

    /** Redux selectors */
    const layout = useSelector(getLayout);
    const options = useSelector(getOptions);
    const { showEmptyCols, showEmptyRows, showSymbols } = options;
    const sorting = useSelector(getSorting);

    // const currentSection = useSelector(getCurrentSection);
    const dimensions = useSelector(getDimensions);
    const infoTypes = useSelector(getInfoTypes);
    const multiSections = useSelector(getMultiSections);
    // const sections = useSelector(getSections);
    const selectedDates = useSelector(getDatesSelected);
    const selectedJTs = useSelector(getSelectedTerritorialUnitsItems);
    const selectedIndicators = useSelector(getSelectedIndicators);
    // const currentIndicator=useSelector(getCurrentIndicator)

    const selectedDimensions = useSelector(getSelectedDimensions);
    const selectedPositions = useSelector(getSelectedPositions);
    const filtersChanged = useSelector(getFiltersChanged);
    const loadedLang = useSelector(getLoadedLang);

    const listJT = useSelector(getSelectedJT);
    const listZm = useSelector(getSelectedWSK);
    const listSC = useSelector(getSelectedSC);

    const loadingModules = useSelector(getLoadingModules);

    /** States */
    const [ABORT, setABORT] = useState(false);
    const [API_DATA, setAPI_DATA] = useState(apiData);
    const [API_DATA_SORTED, setAPI_DATA_SORTED] = useState(null);
    // const [EMPTY, setEMPTY] = useState(null);
    const [EMPTY, setEMPTY] = useState(empty);
    const [ERROR, setERROR] = useState(false);
    const [HEAD_DATA, setHEAD_DATA] = useState(null);
    const [LOADING, setLOADING] = useState(false);
    const [ORDER, setORDER] = useState("asc");
    const [ORDER_BY, setORDER_BY] = useState(null);
    const [QUERY, setQUERY] = useState(null);
    const [PAGE, setPAGE] = useState(0);
    const [ROWS_PER_PAGE, setROWS_PER_PAGE] = useState(10);
    const [ROWS_COUNT, setROWS_COUNT] = useState(0);
    const [SCROLL_TOP, setSCROLL_TOP] = useState(0);
    const [SHOW_SPLIT, setSHOW_SPLIT] = useState(true);
    const [SPANS, setSPANS] = useState({ rowSpan: 1, colSpan: 1 });
    const [SPLIT_WIDTHS, setSPLIT_WIDTHS] = useState(null);
    const [TABLE_PARAMS, setTABLE_PARAMS] = useState(defaultTableParams);
    /** /States */

    /** Effects */
    useEffect(() => {
        dispatch(setFiltersChanged(true));
    }, [i18n.language]);

    /** - loading modules */
    useEffect(() => {
        dispatch(setLoadingModules({ key: "tResults", value: LOADING }));
    }, [LOADING]);

    /** - make QUERY */
    useEffect(() => {
        // process.env.NODE_ENV === "development" &&
        //     console.log("%c make QUERY ", "background: #030; color:#FFF", {
        //         layout,
        //         listJT,
        //     });

        if (loadingDefaults) return;

        if (!!Object.values(loadingModules)?.filter((f) => f)?.length) return;

        if (!(dimensions && selectedDates.length && selectedIndicators.length && selectedJTs.length)) return;

        if (layout?.list?.length) {
            const _layoutListEl = layout?.list?.[0];
            if (_layoutListEl === "JT" && !listJT) return;
            if (_layoutListEl === "SC" && !listSC) return;
            if (_layoutListEl === "Zm" && !listZm) return;
        }

        const oldQuery = JSON.parse(JSON.stringify(QUERY));

        const newQuery = {
            ...emptyQuery,
            ...oldQuery,
            opts: {
                ...emptyQuery.opts,
                blockHead: oldQuery?.opts?.blockHead,
                showSymbols,
                showEmptyRows,
                showEmptyCols,
            },
            params: TABLE_PARAMS,
        };

        newQuery.list = layout.list.flatMap((l) => builder[l]());
        newQuery.rows = layout.rows.flatMap((row) => builder[row]());
        newQuery.cols = layout.cols.flatMap((col, i) => builder[col](i)).filter((f) => f);

        newQuery.rows = newQuery.rows.map((row, idx) => {
            return { ...row, idx };
        });

        if (showSymbols) {
            newQuery.rows = newQuery?.rows?.map((row, i) => ({
                ...row,
                showSymbol: row.type === "POS" || row.type === "JT",
            }));
        }

        newQuery.cols[0] = newQuery.cols[0]
            ? {
                  ...newQuery.cols[0],
                  sort: sorting?.find((s) => s.idx === 999999990)?.order,
                  values_sort: sorting
                      ?.filter((s) => newQuery.cols[0]?.titles?.includes(s.title))
                      ?.map((s) => ({ idx: s.idx, order: s.order })),
                  idx: 999999990,
              }
            : newQuery.cols[0];

        if (newQuery.cols[1]) {
            newQuery.cols[1].idx = 999999991;
            newQuery.cols[1].sort = sorting?.find((s) => s.idx === 999999991)?.order;
        }

        /** Horizontal sort */
        /** - first row in cols */
        if (sorting?.find((s) => s.idx === 999999990)) {
            const _order = sorting?.find((s) => s.idx === 999999990).order;
            if (_order === "desc") {
                newQuery.cols[0]?.titles?.reverse();
                newQuery.cols[0]?.values?.reverse();
            }
        }

        if (sorting?.find((s) => s.idx === 999999991)) {
            const _order = sorting?.find((s) => s.idx === 999999991).order;
            if (_order === "desc") {
                newQuery.cols[1]?.titles?.reverse();
                newQuery.cols[1]?.values?.reverse();
            }
        }
        /** /Horizontal sort */

        let _sort = sorting?.map((s) => ({ idx: s.idx, order: s.order }));

        if (showSymbols && !_sort?.length && TABLE_PARAMS?.sort?.order && TABLE_PARAMS?.sort?.orderBy?.idx) {
            _sort = [{ idx: TABLE_PARAMS?.sort?.orderBy?.idx, order: TABLE_PARAMS?.sort?.order }];
        }

        newQuery.params = { ...TABLE_PARAMS, sort: _sort };

        newQuery.cols = newQuery.cols?.filter((f) => f);

        /**if Zm is in list move TYPE and POS[es] to rows */
        if (layout.list[0] === "Zm") {
            const _source = newQuery.list?.filter((f) => ["TYPE", "POS"].includes(f.type));
            const _target = newQuery.rows?.concat(_source)?.map((t, i) => ({ ...t, idx: i }));
            newQuery.list = newQuery.list?.filter((f) => f.type === "Zm");
            newQuery.rows = _target?.length ? _target : newQuery.rows;
        }

        const _dimensions_lang = newQuery?.rows?.find((r) => r.type === "Zm")?.loaded || i18n.language;

        // process.env.NODE_ENV === "development" &&
        //     console.log("%c newQuery ", "background: #999; color:#FFF", {
        //         newQuery,
        //         QUERY,
        //         _dimensions_lang,
        //         "JSON.stringify(newQuery) === JSON.stringify(QUERY)":
        //             JSON.stringify(newQuery) === JSON.stringify(QUERY),
        //     });

        if (JSON.stringify(newQuery) !== JSON.stringify(QUERY)) {
            // process.env.NODE_ENV === "development" &&
            //     console.log("%c make QUERY processing", "background: #090; color:#FFF", {
            //         // loadingDefaults,
            //         // loadingModules: !!Object.values(loadingModules)?.filter((f) => f)?.length,
            //         newQuery,
            //         QUERY,
            //         // language: i18n.language,
            //     });

            setQUERY(newQuery);
        }
    }, [
        QUERY,
        TABLE_PARAMS, // !! was commented to prevent requery API on table page change
        dimensions,
        infoTypes,
        layout,

        sorting,

        loadingDefaults,
        multiSections,
        selectedDates,
        selectedIndicators,
        selectedJTs,
        showEmptyCols,
        showEmptyRows,
        showSymbols,

        listJT,
        listSC,
        listZm,

        // i18n.language,
        selectedDimensions,
        selectedPositions,
        // filtersChanged,
        loadingModules,
        // loadedLang
    ]);

    /** - set HEAD_DATA */
    useEffect(() => {
        setHEAD_DATA(QUERY);

        const colspan = QUERY?.cols[1]?.values?.length
            ? QUERY?.cols[1]?.type === "JT"
                ? QUERY?.cols[1]?.values?.length * 2
                : QUERY?.cols[1]?.values?.length
            : 1;

        const _spans = {
            rowspan: QUERY?.cols?.length || 1,
            // colspan: QUERY?.cols[1]?.values?.length || 1,
            colspan: QUERY?.cols[1]?.titles?.length || 1,
            // colspan
        };

        setSPANS(_spans);
    }, [QUERY]);

    /** - send HEAD_DATA */
    useEffect(() => onGetHeadData && onGetHeadData(QUERY), [HEAD_DATA]);

    /** - get data from API */
    useEffect(() => {
        if (loadingDefaults || apiData || !QUERY || !abortControllerRef.current) return;

        const _testPOS = [...QUERY?.cols, ...QUERY?.list, ...QUERY?.rows]?.map((item) => item.type)?.includes("POS");
        if (!_testPOS) return;

        if (!window.dtTimer) {
            console.time("time");
            window.dtTimer = true;
        }

        setLOADING(true);
        setERROR(false);
        setAPI_DATA(null);
        onGetRequest && onGetRequest(null);

        const _queryAPI = {
            ...QUERY,
            rows: QUERY.rows.filter((r) => r.type !== "SYMBOL"),
        };

        // process.env.NODE_ENV === "development" && console.log({ QUERY, _queryAPI, _testPOS });

        handleOnAbortApi();

        let signal = abortControllerRef.current?.signal;

        setABORT(false);
        onAbortAPI && onAbortAPI(false);

        console.time("API time");
        // getTableNewManyIndicatorsNewFromAPI(QUERY)
        getTableNewManyIndicatorsNewFromAPI(_queryAPI, signal)
            .then((data) => {
                // process.env.NODE_ENV === "development" &&
                //     console.log("%c getTableNewManyIndicatorsFromAPI ", "background: #999; color:#FFF", data);

                if (!data?.error) {
                    setERROR(false);
                    let CONVERTED = data;

                    if (data?.message?.catched === "The user aborted a request.") {
                        setERROR({ message: t(`Użytkownik wstrzymał żądanie`) });
                        return;
                    } else if (data?.message?.catched === "signal is aborted without reason") {
                        setLOADING(true);
                        console.timeEnd("API time");
                        return;
                    }

                    data.rowsNumber && setROWS_COUNT(data.rowsNumber);

                    data.pageNumber && setPAGE(data.pageNumber);

                    const _empty = !options.showEmptyCols && CONVERTED.empty ? CONVERTED.empty : null;
                    // const _empty = {cols:[5], rows:[]}
                    setEMPTY(_empty);
                    if (_empty?.cols?.length) {
                        const nDATA = CONVERTED.data.map((row) => row.filter((_, i) => !_empty.cols.includes(i)));
                        CONVERTED.data = nDATA;
                    }

                    setAPI_DATA({
                        ...CONVERTED,
                        headData: QUERY,
                    });

                    dispatch(setFiltersChanged(false));

                    onGetLegendData && onGetLegendData(CONVERTED.legend);
                    onGetRequest && onGetRequest(_queryAPI);

                    // process.env.NODE_ENV === "development" && console.log({ CONVERTED });

                    // if (data?.message?.catched === "The user aborted a request.") {
                    //     setERROR({ message: t(`Użytkownik wstrzymał żądanie`) });
                    // }else if (data?.message?.catched === "signal is aborted without reason") {
                    //     setLOADING(true);
                    //     console.timeEnd("API time");
                    // } else if (!CONVERTED?.data?.length) {
                    //     const _errMess = t(`Brak danych`);
                    //     setERROR({ message: _errMess });
                    // }
                    if (!CONVERTED?.data?.length) {
                        const _errMess = t(`Brak danych`);
                        setERROR({ message: _errMess });
                    }
                } else {
                    if (data?.error) {
                        setERROR(data);
                    }
                }
            })
            .catch((e) => {
                console.log(e, e.response);
                setERROR(e.response?.data || { message: "Opps! from component" });
            })
            .finally(() => {
                setLOADING(false);
                process.env.NODE_ENV === "development" && console.log("%c API time: ", "background: #6C6; color:#000");
                console.timeEnd("API time");
                // process.env.NODE_ENV === "development" && console.log("table handleOnApply ?");
                onApply && onApply(true);
            });
    }, [QUERY, abortControllerRef, loadingDefaults]);

    /** - set API_DATA_SORTED */
    useEffect(() => setAPI_DATA_SORTED(API_DATA?.data), [API_DATA]);

    /** - set whether is shown splitted */
    useEffect(() => {
        // process.env.NODE_ENV === "development" &&
        //     console.log(
        //         "%c setSHOW_SPLIT: ",
        //         "background: #333; color:#FFF",
        //         SPLIT_WIDTHS,
        //         SPLIT_WIDTHS?.left?.max + SPLIT_WIDTHS?.right.max - 1,
        //         SPLIT_WIDTHS?.left?.max + SPLIT_WIDTHS?.right.max > SPLIT_WIDTHS?.full
        //     );

        setSHOW_SPLIT(
            isMobile
                ? false
                : options.blockHead && SPLIT_WIDTHS?.left?.max + SPLIT_WIDTHS?.right.max - 1 > SPLIT_WIDTHS?.full
        );
    }, [SPLIT_WIDTHS, options, isMobile]);

    /** reset ORDER_BY on showSymbols */
    useEffect(() => setORDER_BY(null), [showSymbols]);

    useEffect(() => {
        abortControllerRef.current = new AbortController();
    }, []);
    /** /Effects */

    /** Handlers */
    /** - sort data by column */
    const handleRequestSort = (event, property) => {
        const isAsc = sorting.find((s) => s.idx === property.idx && s.title === property.title)?.order;
        const _order = isAsc === "asc" ? "desc" : "asc";

        // process.env.NODE_ENV === "development" && console.log({ property, isAsc, _order });

        setORDER(_order);
        setORDER_BY(property.idx);

        setTABLE_PARAMS({
            ...TABLE_PARAMS,
            sort: {
                order: _order,
                orderBy: property,
            },
        });

        const id = uuidv4();
        dispatch(
            setSorting([
                ...(sorting.filter((s) => s.sort === "horizontal") || []),
                {
                    ...property,
                    id,
                    order: _order,
                    sort: "vertical",
                },
            ])
        );
    };

    /** - change page */
    const handleChangePage = (event, newPage) => {
        setPAGE(newPage);
        setTABLE_PARAMS({
            ...TABLE_PARAMS,
            page: newPage,
            offset: newPage * TABLE_PARAMS.rowsPerPage,
        });
    };

    /** - change rows per page count */
    const handleChangeRowsPerPage = (event) => {
        const _newRowsPerPage = parseInt(event.target.value, 10);
        setROWS_PER_PAGE(_newRowsPerPage);
        setPAGE(0);
        setTABLE_PARAMS({
            ...TABLE_PARAMS,
            page: 0,
            offset: 0,
            rowsPerPage: _newRowsPerPage,
        });
    };

    /** - handle widths change */
    const handleOnGetWidths = ({ widths }) => {
        if (JSON.stringify(widths) !== JSON.stringify(SPLIT_WIDTHS)) {
            setSPLIT_WIDTHS(widths);
        }
    };

    const handleTableScroll = (event, args) => {
        const _scrollTop = event?.currentTarget?.scrollTop;
        const _scrollHeight = event?.currentTarget?.scrollHeight;
        // console.log("handleTableScroll", { _scrollTop, _scrollHeight });
        // setSCROLL_TOP(_scrollTop);
    };

    const handleOnAbortApi = () => {
        setABORT(true);
        onAbortAPI && onAbortAPI(true);
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
    };
    /** /Handlers */

    /** Consts */
    const emptyQuery = {
        opts: { ...(options || {}), lang: i18n.language },
        list: [],
        rows: [],
        cols: [],
    };

    const hasAvgInRow = false;

    const hasMedInRow = false;

    const builder = {
        JT: (index) => {
            return [
                {
                    type: "JT",
                    title: t("Jednostka terytorialna"),
                    title_orig: "Jednostka terytorialna",
                    values: selectedJTs
                        .map((jt) => jt.id_pozycja)
                        .filter((f) => (listJT ? f === listJT.id_pozycja : true)),
                    titles: selectedJTs
                        .map((jt) => translationFromJson(jt, "nazwa"))
                        .filter((f) => (listJT ? f === translationFromJson(listJT, "nazwa") : true)),
                    titles_orig: selectedJTs
                        .map((jt) => jt?.nazwa)
                        .filter((f) => (listJT ? f === translationFromJson(listJT, "nazwa") : true)),
                    titlesExt: selectedJTs
                        .map((jt) => `${translationFromJson(jt, "nazwa")} (${jt.sym_kts})`)
                        .filter((f) => (listJT ? f === translationFromJson(listJT, "nazwa") : true)),
                    idx: 0,
                },
            ];
        },
        Zm: () => {
            const result = [
                {
                    type: "Zm",
                    title: t("Zmienna"),
                    title_orig: "Zmienna",
                    values: selectedIndicators
                        .map((i) => i.indicator_id)
                        .filter((f) => (listZm ? f === listZm.indicator_id : true)),
                    titles: selectedIndicators
                        .map((i) => translationFromJson(i, "name").replace(/(<([^>]+)>)|\t|\n/gi, ""))
                        .filter((f) =>
                            listZm ? f === translationFromJson(listZm, "name").replace(/(<([^>]+)>)|\t|\n/gi, "") : true
                        ),
                    titles_orig: selectedIndicators
                        .map((i) => i?.name?.replace(/(<([^>]+)>)|\t|\n/gi, ""))
                        .filter((f) =>
                            listZm ? f === translationFromJson(listZm, "name").replace(/(<([^>]+)>)|\t|\n/gi, "") : true
                        ),
                    idx: 0,
                },
                {
                    type: "TYPE",
                    title: t("Typ informacji"),
                    title_orig: "Typ informacji",
                    values: [],
                    new_values: [],
                    titles: [],
                    idx: 1,
                },
            ];

            let idx = 1;

            const _dim_indicators = selectedIndicators
                .map((i) => i.indicator_id)
                .filter((f) => (listZm ? f === listZm.indicator_id : true));

            _dim_indicators.forEach((indicator_id) => {
                const _dim_indicator_sections = multiSections[indicator_id];

                _dim_indicator_sections?.forEach((section_id) => {
                    const _info_type =
                        infoTypes &&
                        infoTypes[indicator_id]?.find((t) => t.id_section?.toString() === section_id?.toString());

                    const _info_types =
                        (infoTypes &&
                            infoTypes[indicator_id]?.filter(
                                (t) => t.id_section?.toString() === section_id?.toString() && t.checked
                            )) ||
                        [];

                    result[1].values = _.uniq(
                        [...result[1].values, _info_type?.id_presentation || ""].filter((f) => f)
                    );
                    result[1].new_values = _.uniq(
                        [...result[1].new_values, ..._info_types.flatMap((it) => it.id_presentation)].filter((f) => f)
                    );

                    result[1].values = result[1].new_values;

                    result[1].titles = _.uniq([...result[1].titles, translationFromJson(_info_type, "name")]);

                    const _dimensions =
                        dimensions && dimensions[indicator_id] && dimensions[indicator_id][section_id]
                            ? dimensions[indicator_id][section_id]
                            : false;

                    idx++;
                    if (_dimensions) {
                        if (_dimensions?.length) {
                            _dimensions.forEach((dimension) => {
                                // const title = translationFromJson(dimension, "name")?.replace(
                                //     /(<([^>]+)>)|\t|\n/gi,
                                //     ""
                                // );
                                const title = translationFromJson(dimension, "gr_dimension_name")?.replace(
                                    /(<([^>]+)>)|\t|\n/gi,
                                    ""
                                );
                                const title_orig = dimension?.gr_dimension_name?.replace(/(<([^>]+)>)|\t|\n/gi, "");

                                // const loaded = dimension.loaded;
                                const loaded = !!dimension.loaded;
                                result[0].loaded = loaded;
                                const posIndex = result.findIndex((r) => r.title === title);

                                // console.log(` ----------------------------------------`)
                                // console.log(`_dimensions.forEach ~ title:`, title, dimension)
                                // console.log(` ----------------------------------------`)

                                if (posIndex > -1) {
                                    result[posIndex].values = _.uniq([
                                        ...result[posIndex].values,
                                        ...(_info_types
                                            ?.filter(
                                                (it) => it // it.id_variable === indicator_id && it.id_section === section_id
                                            )
                                            ?.flatMap((it) => {
                                                const _dimension_id = dimension?.children?.[0].dimension_id;

                                                const _selected_positions_by_dimension_id = selectedPositions?.filter(
                                                    (s) =>
                                                        s.indicator_id === indicator_id &&
                                                        s.section_id === section_id &&
                                                        s.dimension_id === _dimension_id
                                                );

                                                // return getAllIDs(dimension.children)
                                                //     ?.filter((c) => c.checked)
                                                return _selected_positions_by_dimension_id?.map(
                                                    (c) =>
                                                        `${indicator_id};${section_id};${it.id_presentation};${
                                                            c.dimension_id || _dimension_id
                                                        }.${c.position_id}`
                                                );
                                            }) || []),
                                    ]);
                                    result[posIndex].titles = [];
                                } else {
                                    const res = {
                                        type: "POS",
                                        section_id,
                                        title,
                                        title_orig,
                                        loaded,
                                        values: _info_types
                                            ?.filter(
                                                (it) => it // it.id_variable === indicator_id && it.id_section === section_id
                                            )
                                            ?.flatMap((it) => {
                                                const _dimension_id = dimension?.children?.[0].dimension_id;

                                                const _selected_positions_by_dimension_id = selectedPositions?.filter(
                                                    (s) =>
                                                        s.indicator_id === indicator_id &&
                                                        s.section_id === section_id &&
                                                        s.dimension_id === _dimension_id
                                                );

                                                // return getAllIDs(dimension.children)
                                                //     ?.filter((c) => c.checked)
                                                return _selected_positions_by_dimension_id?.map(
                                                    (c) =>
                                                        `${indicator_id};${section_id};${it.id_presentation};${
                                                            c.dimension_id || _dimension_id
                                                        }.${c.position_id}`
                                                );
                                            }),
                                        titles: [],
                                        // idx: 0,
                                        idx,
                                    };
                                    result.push(res);
                                }
                                idx++;
                            });
                        } else {
                            // process.env.NODE_ENV === "development" &&
                            //     console.log(indicator_id, section_id, _info_types);
                            const res = {
                                type: "POS",
                                section_id,
                                title: t("Brak wymiarów"),
                                title_orig: "Brak wymiarów",
                                values: _info_types
                                    .flatMap((it) => it)
                                    .filter((it) => it.checked)
                                    .map((it) => `${indicator_id};${section_id};${it.id_presentation};`),
                                new_values: [],
                                titles: [],
                                // idx: 0,
                                idx,
                            };
                            result.push(res);
                        }
                    }
                });
            });

            return result;
        },
        SC: () => {
            return [
                {
                    type: "SC",
                    title: t("Szereg czasowy"),
                    title_orig: "Szereg czasowy",
                    values: [...selectedDates]
                        .filter((f) => f.enabled)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .flatMap((d) => d.tab_period_id)
                        .filter((f) => (listSC ? f === listSC.tab_period_id[0] : true)),
                    titles: [...selectedDates]
                        .filter((f) => f.enabled)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .flatMap((d) => translationFromJson(d, "name"))
                        .filter((f) => (listSC ? f === listSC.name : true)),
                    titles_orig: [...selectedDates]
                        .filter((f) => f.enabled)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .flatMap((d) => d?.name)
                        .filter((f) => (listSC ? f === listSC.name : true)),
                    years: [...selectedDates]
                        .filter((f) => f.enabled)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .flatMap((d) => d.date_id)
                        .filter((f) => (listSC ? f === listSC.date_id : true)),
                    idx: 0,
                },
            ];
        },
    };

    return (
        <>
            {/* <pre>{JSON.stringify(sorting, null, 4)}</pre> */}
            {/* <pre>{JSON.stringify(ORDER_BY, null, 4)}</pre> */}
            {/* <pre>{JSON.stringify(ERROR, null, 4)}</pre> */}
            {/* <pre>{JSON.stringify(loadingModules, null, 4)}</pre> */}
            {/* <pre>{JSON.stringify(QUERY)}</pre> */}

            <Box sx={{ width: "100%" }}>
                {!!LOADING && <ProgressAPI preloader={false} onAbortAPI={handleOnAbortApi} sx={{ mb: 4 }} />}

                {(!!Object.values(loadingModules)?.filter((f) => f)?.length || !QUERY) && (
                    <>
                        {/* <ProgressAPI
                            title={`${t("Parametry")}`}
                            onAbortAPI={handleOnAbortApi}
                            loadingModules={loadingModules}
                        /> */}
                        <Preloader type={"linear"} style={{ marginBottom: 16, width: "100%" }} />
                    </>
                )}

                {/* {!QUERY && (
                    <>
                        <ProgressAPI title={`${t("Parametry")}`} onAbortAPI={handleOnAbortApi} />
                        <Preloader type={"linear"} />
                    </>
                )} */}

                {/* {!!LOADING && <ProgressAPI title={`${t("Dane")}`} onAbortAPI={handleOnAbortApi} />} */}

                <Paper sx={{ width: "100%", mb: 2, position: "relative", zIndex: 0 }} ref={paperRef} elevation={0}>
                    {
                        // !!! CHANGE IT (remove `|| ERROR`) !!!!!!!!!!!!!
                        !!LOADING && (
                            // (!!LOADING || !!ERROR)&&
                            <MuiTable
                                dense={true}
                                empty={EMPTY}
                                query={QUERY}
                                hasAvgInRow={hasAvgInRow}
                                hasMedInRow={hasMedInRow}
                                headData={HEAD_DATA}
                                isInDialog={isInDialog}
                                loading={LOADING}
                                onRequestSort={handleRequestSort}
                                order={ORDER}
                                orderBy={ORDER_BY}
                                page={PAGE}
                                pageLength={pageLength}
                                rows={API_DATA_SORTED}
                                rowsPerPage={ROWS_PER_PAGE}
                                spans={SPANS}
                                splitView={"left"}
                                tableData={API_DATA_SORTED}
                                scrolling={(event) => handleTableScroll(event)}
                            />
                        )
                    }
                    {!!ERROR && (
                        <Alert variant="outlined" severity="error">
                            {translationFromJson(ERROR.message, "text") || translationFromJson(ERROR, "message")}
                        </Alert>
                    )}

                    {!!API_DATA_SORTED?.length && !LOADING && !ERROR &&  (
                        <>
                            {!!SHOW_SPLIT && (
                                <SplitView
                                    left={
                                        <MuiTable
                                            dense={true}
                                            empty={EMPTY}
                                            hasAvgInRow={hasAvgInRow}
                                            hasMedInRow={hasMedInRow}
                                            headData={HEAD_DATA}
                                            isInDialog={isInDialog}
                                            onRequestSort={handleRequestSort}
                                            order={ORDER}
                                            orderBy={ORDER_BY}
                                            page={PAGE}
                                            pageLength={pageLength}
                                            query={QUERY}
                                            rows={API_DATA_SORTED}
                                            rowsPerPage={ROWS_PER_PAGE}
                                            showSplit={SHOW_SPLIT}
                                            spans={SPANS}
                                            splitView={"left"}
                                            tableData={API_DATA_SORTED}
                                            scrolling={(event) => handleTableScroll(event)}
                                        />
                                    }
                                    right={
                                        <MuiTable
                                            dense={true}
                                            empty={EMPTY}
                                            hasAvgInRow={hasAvgInRow}
                                            hasMedInRow={hasMedInRow}
                                            headData={HEAD_DATA}
                                            isInDialog={isInDialog}
                                            onRequestSort={handleRequestSort}
                                            order={ORDER}
                                            orderBy={ORDER_BY}
                                            page={PAGE}
                                            pageLength={pageLength}
                                            query={QUERY}
                                            rows={API_DATA_SORTED}
                                            rowsPerPage={ROWS_PER_PAGE}
                                            showSplit={SHOW_SPLIT}
                                            spans={SPANS}
                                            splitView={"right"}
                                            tableData={API_DATA_SORTED}
                                            scrolling={(event) => handleTableScroll(event)}
                                        />
                                    }
                                    widths={SPLIT_WIDTHS}
                                    showSplit={SHOW_SPLIT}
                                    isInDialog={isInDialog}
                                />
                            )}

                            <MuiTable
                                dense={true}
                                empty={EMPTY}
                                hasAvgInRow={hasAvgInRow}
                                hasMedInRow={hasMedInRow}
                                headData={HEAD_DATA}
                                isInDialog={isInDialog}
                                onGetWidths={(widths) => handleOnGetWidths({ widths })}
                                onRequestSort={handleRequestSort}
                                order={ORDER}
                                orderBy={ORDER_BY}
                                page={PAGE}
                                pageLength={pageLength}
                                query={QUERY}
                                rows={API_DATA_SORTED}
                                rowsPerPage={ROWS_PER_PAGE}
                                showSplit={SHOW_SPLIT}
                                spans={SPANS}
                                splitView={"none"}
                                tableData={API_DATA_SORTED}
                                widths={SPLIT_WIDTHS}
                                scrolling={(event) => handleTableScroll(event)}
                            />
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                                component="div"
                                count={ROWS_COUNT || API_DATA_SORTED?.length}
                                rowsPerPage={ROWS_PER_PAGE}
                                page={PAGE}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelDisplayedRows={({ from, to, count }) => {
                                    return `${from}–${to} ${t("z")} ${count !== -1 ? count : `${t("ponad")} ${to}`}`;
                                }}
                                // labelRowsPerPage={`${t("Liczba wierszy na stronie")}:`}
                                labelRowsPerPage={t("Wierszy na stronę{{colon}}", { colon: ":" })}
                                // ActionsComponent={TablePaginationActions}
                                sx={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}
                                className={classes.tablePagination}
                                classes={{
                                    root: classes.tablePagination,
                                    select: classes.tablePaginationSelect,
                                    selectLabel: classes.tablePaginationSelectLabel,
                                    toolbar: classes.tablePaginationToolbar,
                                    displayedRows: classes.tablePaginationDisplayedRows,
                                    actions: classes.tablePaginationActions,
                                }}
                            />
                        </>
                    )}
                </Paper>
            </Box>

            {zoom && API_DATA && (
                <TableDialog open={zoom} onClose={(zoom) => onZoom(zoom)}>
                    <MuiTableIndex
                        query={QUERY}
                        // apiData={API_DATA}
                        isInDialog={true}
                        // empty={EMPTY}
                    />
                    {!!API_DATA?.legend && <Legend legend={API_DATA?.legend} />}
                </TableDialog>
            )}
        </>
    );
};

export default MuiTableIndex;

const TablePaginationActions = (props) => {
    const { count, page, rowsPerPage, onPageChange } = props;
    const { t } = useTranslation();

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1);
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <AppTooltip title={t("Pokaż poprzednią stronę")} arrow placement={"top"} type={"dark"}>
                <span>
                    <IconButton
                        onClick={handleBackButtonClick}
                        disabled={page === 0}
                        aria-label={t("Pokaż poprzednią stronę tabeli")}
                    >
                        <KeyboardArrowLeft />
                    </IconButton>
                </span>
            </AppTooltip>

            <AppTooltip title={t("Pokaż następną stronę")} arrow placement={"top-end"} type={"dark"}>
                <span>
                    <IconButton
                        onClick={handleNextButtonClick}
                        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                        aria-label={t("Pokaż następną stronę tabeli")}
                    >
                        <KeyboardArrowRight />
                    </IconButton>
                </span>
            </AppTooltip>
        </Box>
    );
};
